// src/components/Products/ProductCard.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';

const ProductCard = ({ product }) => {
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { addToCart, getCartItem, increaseQuantity, decreaseQuantity } = useCart();

  const vId = String(product.outletVariantId || product.variantId || product.id || '');
  const productId = product.productId || product.id || vId;
  
  const name = product.productName || product.name || product.description || 'Product';
  const price = product.sellingPrice || product.price || 0;
  const mrp = product.mrp || product.originalPrice || price;
  const imageUrl = product.image || (product.images && product.images[0]) || product.imageUrl || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=300&h=300&fit=crop&crop=center';
  const pack = product.variantName || product.pack || product.unit || '1 Pack';
  
  // NEW: Determine stock status directly from the product object
  const stock = product.stock !== undefined ? product.stock : 1;
  const isOutOfStock = stock === 0;

  // Delivery Type Logic
  const orderType = product.orderType || 'QUICK';
  const isStandard = orderType.toUpperCase() === 'STANDARD';

  // NEW: Added mode in URL ensuring external/direct links respect the mode
  const detailUrl = `/product/${productId}?vid=${vId}&mode=${orderType}`;

  const isFavorite = isInWishlist(vId);
  const cartItem = getCartItem(product);

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOutOfStock) {
      addToCart(product);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col relative group h-full overflow-hidden">
      
      {/* Top Controls: Wishlist Heart & Add Button */}
      <div className="absolute top-2 left-2 z-30">
        <button onClick={handleWishlistClick} className="p-1">
          <i className={`fas fa-heart text-xl drop-shadow-sm transition-colors ${isFavorite ? 'text-red-500' : 'text-gray-300 hover:text-gray-400'}`}></i>
        </button>
      </div>

      {/* FIXED: Hide Add to Cart completely if Out of Stock */}
      {!isOutOfStock && (
        cartItem ? (
          <div className="absolute top-2 right-2 z-30 flex items-center justify-between bg-white border border-[#0078ad] rounded-md shadow-sm h-8 w-[76px] overflow-hidden">
             <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); decreaseQuantity(cartItem); }} className="w-7 h-full flex items-center justify-center text-[#0078ad] hover:bg-sky-50 font-bold bg-white text-lg leading-none">-</button>
             <span className="flex-1 text-center text-xs font-bold text-gray-800 bg-white leading-8">{cartItem.quantity}</span>
             <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); increaseQuantity(cartItem); }} className="w-7 h-full flex items-center justify-center text-[#0078ad] hover:bg-sky-50 font-bold bg-white text-lg leading-none">+</button>
          </div>
        ) : (
          <div className="absolute top-2 right-2 z-30">
            <button 
                 onClick={handleAddClick} 
                 className="bg-white text-[#0078ad] border border-[#0078ad] px-4 py-1.5 rounded-md shadow-sm text-sm font-bold hover:bg-[#f0f9ff] transition-colors leading-none"
            >
              Add
            </button>
          </div>
        )
      )}

      {/* Product Image */}
      <Link to={detailUrl} state={{ product }} className="relative block pt-10 pb-3 px-4 flex-shrink-0 bg-white">
        <div className="aspect-square flex items-center justify-center relative">
          <img 
               src={imageUrl} 
               alt={name} 
               className={`max-w-full max-h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-300 ${isOutOfStock ? 'opacity-40' : ''}`}
          />
          {/* NEW: Clean Overlay for OOS */}
          {isOutOfStock && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-white/95 px-2.5 py-1.5 rounded-lg shadow-sm border border-gray-200 pointer-events-none">
              <span className="text-[10px] font-black text-red-500 uppercase tracking-widest whitespace-nowrap">Out of Stock</span>
            </div>
          )}
        </div>
      </Link>

      {/* Pack Size / Variant Band */}
      <div className="bg-[#fff9e6] py-1.5 px-2 text-center border-y border-gray-100 flex items-center justify-center gap-1">
        <span className="text-gray-700 text-xs font-medium">{pack}</span>
        <i className="fas fa-chevron-down text-[8px] text-gray-500 mt-0.5"></i>
      </div>

      {/* Details Section */}
      <Link to={detailUrl} state={{ product }} className="p-3 flex flex-col flex-1 bg-white">
        <h3 className={`text-gray-700 text-[13px] leading-snug line-clamp-2 min-h-[36px] mb-3 font-medium ${isOutOfStock ? 'opacity-60' : ''}`}>
          {name}
        </h3>
        
        <div className="mt-auto flex items-end gap-2 mb-2">
          <span className={`font-extrabold text-[17px] ${isOutOfStock ? 'text-gray-400' : 'text-black'}`}>₹ {price}</span>
          {mrp > price && (
            <span className="text-gray-400 text-xs line-through mb-0.5 font-medium">₹ {mrp}</span>
          )}
        </div>

        {/* FIXED: Dynamic Delivery Tag logic rendering safely */}
        {!isOutOfStock && (
          <div className="flex items-center gap-1 mt-1">
            <i className={`fas ${isStandard ? 'fa-truck text-amber-500' : 'fa-bolt text-orange-500'} text-[11px]`}></i>
            <span className={`${isStandard ? 'text-amber-500' : 'text-orange-500'} text-[11px] font-bold tracking-wide`}>
              {isStandard ? 'Standard Delivery' : 'Quick Delivery'}
            </span>
          </div>
        )}
      </Link>
    </div>
  );
};

export default ProductCard;